import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/shadcn/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/shadcn/popover'

export type TimezoneOption = [label: string, value: string]

interface TimezoneSelectProps {
  value: string | null | undefined
  options: TimezoneOption[]
  onChange: (value: string) => void
  placeholder?: string
  ariaLabel?: string
  disabled?: boolean
}

export function TimezoneSelect({
  value,
  options,
  onChange,
  placeholder = 'Select a timezone',
  ariaLabel = 'Timezone',
  disabled,
}: TimezoneSelectProps) {
  const [open, setOpen] = useState(false)
  const current = options.find(([, v]) => v === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className="w-full justify-between font-normal cursor-pointer"
        >
          <span className={cn('truncate', !current && 'text-muted-foreground')}>
            {current ? current[0] : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command
          filter={(itemValue, search) => {
            // itemValue is the option's `value` prop (lowercased by cmdk); we
            // attach the human label there so search hits the city name.
            return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }}
        >
          <CommandInput placeholder="Search timezones..." />
          <CommandList>
            <CommandEmpty>No matching timezone.</CommandEmpty>
            {options.map(([label, optionValue]) => (
              <CommandItem
                key={optionValue}
                value={`${label} ${optionValue}`}
                onSelect={() => {
                  onChange(optionValue)
                  setOpen(false)
                }}
                className="cursor-pointer"
              >
                <Check
                  className={cn(
                    'mr-2 size-4',
                    optionValue === value ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
